import { useState, useEffect, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../lib/api.js";

interface SetupData {
  secret: string;
  uri: string;
}

const inp: React.CSSProperties = {
  width: "100%", padding: "10px 12px", border: "1.5px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: 16, letterSpacing: 8, textAlign: "center",
  outline: "none", boxSizing: "border-box", background: "var(--color-surface-0)",
};

export default function SetupMfaPage() {
  const navigate = useNavigate();
  const [setup, setSetup] = useState<SetupData | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get<SetupData>("/auth/mfa/setup")
      .then(r => { setSetup(r.data); setLoading(false); })
      .catch(e => {
        const msg = errMsg(e);
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 409) {
          // Already enabled — skip to onboarding
          navigate("/onboarding/wizard", { replace: true });
        } else {
          setError(msg ?? "Failed to initiate MFA setup.");
          setLoading(false);
        }
      });
  }, [navigate]);

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    setVerifying(true); setError("");
    try {
      await api.post("/auth/mfa/verify-setup", { code });
      navigate("/onboarding/wizard", { replace: true });
    } catch (err: unknown) {
      const msg = errMsg(err);
      setError(msg ?? "Verification failed. Please try again.");
    }
    setVerifying(false);
  }

  async function copySecret() {
    if (!setup) return;
    await navigator.clipboard.writeText(setup.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <span>Setting up MFA…</span>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--color-surface-50)", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 480, background: "var(--color-surface-0)", borderRadius: "var(--radius-xl)", boxShadow: "var(--shadow-md)", padding: "40px 32px" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, background: "var(--color-brand-50)",
            display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-brand-600)", margin: "0 auto 16px",
          }}><Lock size={28} /></div>
          <h1 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 700 }}>Set Up Google Authenticator</h1>
          <p style={{ margin: 0, fontSize: 14, color: "var(--color-surface-700)", lineHeight: 1.5 }}>
            Your account requires two-factor authentication for security. Please set it up before continuing.
          </p>
        </div>

        {/* Steps */}
        <div style={{ marginBottom: 28 }}>
          {[
            "Install Google Authenticator on your phone (iOS or Android)",
            "Open the app and tap the + button",
            'Select "Enter a setup key" and type the key below',
          ].map((step, i) => (
            <div key={i} style={{ display: "flex", gap: 12, marginBottom: 12 }}>
              <div style={{
                width: 24, height: 24, borderRadius: "50%", background: "var(--color-brand-600)",
                color: "#fff", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>{i + 1}</div>
              <p style={{ margin: 0, fontSize: 14, color: "var(--color-surface-900)", lineHeight: 1.5, paddingTop: 3 }}>{step}</p>
            </div>
          ))}
        </div>

        {/* Secret key display */}
        {setup && (
          <div style={{ background: "var(--color-surface-50)", border: "1.5px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "16px 20px", marginBottom: 24 }}>
            <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, color: "var(--color-surface-700)", textTransform: "uppercase", letterSpacing: 0.5 }}>Setup Key</p>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <code style={{ fontSize: 18, fontFamily: "monospace", fontWeight: 700, letterSpacing: 4, color: "var(--color-surface-900)", flex: 1, wordBreak: "break-all" }}>
                {setup.secret.match(/.{1,4}/g)?.join(" ")}
              </code>
              <button
                onClick={copySecret}
                style={{
                  background: "none", border: "1px solid var(--color-surface-300)", borderRadius: "var(--radius-sm)",
                  padding: "4px 10px", fontSize: 12, cursor: "pointer", color: "var(--color-surface-700)", flexShrink: 0,
                  display: "inline-flex", alignItems: "center", gap: 4,
                }}>
                {copied ? <><CheckCircle2 size={12} /> Copied</> : "Copy"}
              </button>
            </div>
            <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--color-surface-700)" }}>
              Account name: <strong>{setup.uri.split("%3A")[1]?.split("?")[0] ?? "HRMS"}</strong> | Issuer: <strong>HRMS</strong>
            </p>
          </div>
        )}

        {/* Verify form */}
        <form onSubmit={handleVerify}>
          <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 8, color: "var(--color-surface-900)" }}>
            Step 4: Enter the 6-digit code shown in Google Authenticator
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            style={inp}
            autoComplete="one-time-code"
          />
          {error && (
            <p style={{ fontSize: 13, color: "var(--color-error)", marginTop: 8, padding: "8px 12px", background: "var(--color-error-bg)", borderRadius: "var(--radius-sm)" }}>
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={verifying || code.length !== 6}
            style={{
              width: "100%", marginTop: 16, padding: "12px",
              background: code.length === 6 ? "var(--color-brand-600)" : "var(--color-surface-200)",
              color: code.length === 6 ? "#fff" : "var(--color-surface-700)",
              border: "none", borderRadius: "var(--radius-md)", fontSize: 15, fontWeight: 700,
              cursor: code.length === 6 ? "pointer" : "not-allowed", transition: "all .15s",
            }}>
            {verifying ? "Verifying…" : "Enable & Continue →"}
          </button>
        </form>

        <p style={{ fontSize: 12, color: "var(--color-surface-700)", textAlign: "center", marginTop: 16 }}>
          Once MFA is enabled you will be required to enter a code on every login.
        </p>
      </div>
    </div>
  );
}
